import { Observable, throwError, from } from 'rxjs';
import { catchError, tap, switchMap } from 'rxjs/operators';
import { Injectable, inject } from '@angular/core';
import { HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { PlatformBrowserService, PlatformInfoService, AuthService } from '@tgx/shared/services';
import { WEB_BFF_AUTH_ENDPOINTS, WEB_BFF_ENDPOINTS } from '@tgx/shared/constants';

@Injectable()
export class HttpHeadersInterceptor implements HttpInterceptor {
  private platformBrowserService = inject<PlatformBrowserService>(PlatformBrowserService);
  private platformInfoService = inject(PlatformInfoService);
  private authService = inject(AuthService);

  // List of domains that will get the authentication header set by the interceptor
  private readonly bearerAuthenticatedDomains = ['travelgate', 'travelgatex', 'localhost'];

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Handle both relative and absolute URLs
    let reqUrl: string;
    let reqPath: string;
    try {
      const url = new URL(req.url);
      reqUrl = url.hostname;
      reqPath = url.pathname;
    } catch {
      // If URL parsing fails (relative URLs), skip authentication checks
      const reqClone = req.clone();
      return next.handle(reqClone).pipe(
        tap(() => {}),
        catchError((err: any) => {
          const requestAppPath = this.platformInfoService.getAppPath();
          const error = { ...err, requestAppPath: requestAppPath };
          return throwError(() => error);
        }),
      );
    }

    const domainRequiresAuthentication = this.bearerAuthenticatedDomains.some((domain) => reqUrl.includes(domain));

    const requestAppPath = this.platformInfoService.getAppPath();

    let headers: HttpHeaders = req.headers;
    if (domainRequiresAuthentication) {
      // Exclude auth0 endpoints from token expiration check to prevent infinite loops
      const isAuthEndpoint =
        reqPath.includes(WEB_BFF_AUTH_ENDPOINTS.LOGOUT) ||
        reqPath.includes(WEB_BFF_AUTH_ENDPOINTS.LOGIN_URL) ||
        reqPath.includes(WEB_BFF_ENDPOINTS.VALIDATE_SESSION);

      if (!isAuthEndpoint && this.platformBrowserService.isTokenExpired()) {
        return this.sessionExpired();
      }

      if (!req.headers.getAll('Authorization')?.length) {
        headers = headers.set('Authorization', `Bearer ${this.platformBrowserService.getToken()}`);
      }

      if (!req.headers.getAll('Content-Type')?.length) {
        headers = headers.set('Content-Type', 'application/json');
      }

      const tgxReferer = headers.get('TGX-Referer');
      // The "w" indicates that the request has passed through the web
      if (tgxReferer) {
        // If the header exists and does not end with "w", append a "w" to its value
        if (!tgxReferer.endsWith('w')) {
          headers = headers.set('TGX-Referer', tgxReferer + 'w');
        }
      } else {
        // If the header does not exist, set its value to "w"
        headers = headers.set('TGX-Referer', 'w');
      }

      const query = req?.body?.query;

      if (query?.startsWith('mutation')) {
        headers = headers.set('TGX-Audit', '1');
      }

      if (this.isB2BQuery(query)) {
        headers = headers.set('TGX-Origin', 'B2B');

        if (this.isB2BCancelOrBookingQuery(query)) {
          headers = headers.set('TGX-B2B', '1');
        }
      }
    }

    // Only include credentials (cookies) for requests to our own BFF
    // Other APIs (like api.travelgate.com) use Bearer tokens and don't need cookies
    const isOurAppDomain =
      reqUrl.includes('dev-app.travelgatex.com') ||
      reqUrl.includes('staging-app.travelgatex.com') ||
      reqUrl.includes('app.travelgate.com') ||
      reqUrl.includes('localhost');

    const isBffRequest = isOurAppDomain && reqPath.startsWith('/api/');

    const reqClone = req.clone({
      headers: headers,
      withCredentials: isBffRequest,
    });

    return next.handle(reqClone).pipe(
      tap(() => {}),
      catchError((err: any) => {
        const error = { ...err, requestAppPath: requestAppPath };
        return throwError(() => error);
      }),
    );
  }

  private sessionExpired(): Observable<never> {
    return from(this.authService.logout()).pipe(switchMap(() => throwError(() => new Error('Token expired'))));
  }

  private isB2BQuery(query: string): boolean {
    return query?.startsWith('query b2b') || query?.startsWith('mutation b2b');
  }

  private isB2BCancelOrBookingQuery(query: string): boolean {
    return query?.startsWith('mutation b2bCancelBooking') || query?.startsWith('query b2bBooking');
  }
}
